'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAccessToken } from '@/lib/auth-client';
import { documentSummaryQueryKey } from '@/components/editor-tabs/document-summary';
import type { FileEntry, FileEntryKind } from 'pc-types';
import { toast } from 'sonner';
import {
  IconDotsVertical,
  IconDownload,
  IconExternalLink,
  IconEye,
  IconFile,
  IconFileTypeDocx,
  IconFileTypePdf,
  IconFileTypeXls,
  IconGripVertical,
  IconInbox,
  IconPencil,
  IconFolderSymlink,
  IconSparkles,
  IconTrash,
  IconX,
} from '@tabler/icons-react';
import { DeckDocIcon, SheetDocIcon } from '@/components/icons/doc-icons';

import { Button } from '@/components/ui/button';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ProcessingStatusBadge } from '@/components/processing-status-badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useDelayedFlag } from '@/lib/use-delayed-flag';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { authHeaders, unwrapLambdaResponse } from '@/lib/api';
import { BACKEND_URL } from '@/lib/constants';
import { reportError } from '@/lib/report-error';
import { cn, formatRelativeTime } from '@/lib/utils';
import type { EntryFilter } from '@/lib/use-file-entries';
import {
  MoveToSpaceDialog,
  type PickerTarget,
} from './space-folder-picker';

export interface DraggedEntry {
  kind: FileEntryKind;
  id: string;
  name: string;
  spaceId: string;
  folderId: string | null;
}

export const DRAG_ENTRY_MIME = 'application/space-documents';

interface EntryListProps {
  // null = the cross-space "All Documents" aggregate; per-row actions then
  // target each entry's own space via entry.spaceId.
  spaceId: string | null;
  // Changes when the user navigates to a different space/folder; the
  // selection belongs to the location it was made in, so it resets.
  locationKey: string;
  entries: FileEntry[];
  isLoading: boolean;
  // Per-entry: on the aggregate, rows from different spaces carry different
  // write access, so a single view-level boolean can't gate them.
  canWriteEntry?: (entry: FileEntry) => boolean;
  /** Demo space rows carry tour anchors (demo-doc-sheet / demo-doc-deck). */
  isDemoSpace?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onFetchNextPage?: () => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  /** Rendered below the rows, inside the list's scroll area. */
  footer?: React.ReactNode;
  /** Replaces the "Nothing here yet" message when there are no rows. */
  emptyState?: React.ReactNode;
}

export function getFileIcon(fileType: string) {
  switch (fileType.toLowerCase()) {
    case 'pdf':
      return <IconFileTypePdf className="size-4 text-file-pdf" />;
    case 'docx':
    case 'doc':
      return <IconFileTypeDocx className="size-4 text-file-doc" />;
    case 'xlsx':
    case 'xls':
      return <IconFileTypeXls className="size-4 text-file-xls" />;
    default:
      return <IconFile className="size-4 text-fg-tertiary" />;
  }
}

const FILE_STATUS_TOOLTIPS: Record<string, string> = {
  pending:
    'Queued for reading. Pitchcrow reads each upload so its contents can be searched and used in decks and models.',
  processing:
    'Pitchcrow is reading this file. When it finishes, its contents can be searched and used in decks and models — large documents can take a few minutes.',
  failed:
    "Pitchcrow couldn't read this file, so its contents can't be used in decks or models. You can still view and download it. Retry, or upload it again.",
};

function entryKindLabel(entry: FileEntry): string {
  switch (entry.kind) {
    case 'deck':
      return 'Deck';
    case 'workbook':
      return 'Sheet';
    case 'file':
      return entry.fileType.toUpperCase();
  }
}

function countLabel(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`;
}

// The move dialog preselects the subjects' shared location; a mixed-folder
// selection gets none, so Move stays disabled until an explicit pick.
function commonLocation(list: FileEntry[]): PickerTarget | null {
  if (list.length === 0) return null;
  const { spaceId, folderId = null } = list[0];
  const shared = list.every(
    (entry) =>
      entry.spaceId === spaceId && (entry.folderId ?? null) === folderId
  );
  return shared ? { spaceId, folderId } : null;
}

function EntryIcon({ entry }: { entry: FileEntry }) {
  if (entry.kind === 'workbook') {
    return <SheetDocIcon className="size-4 text-doc-sheet" />;
  }
  if (entry.kind === 'file') {
    return getFileIcon(entry.fileType);
  }
  if (entry.thumbnailUrl) {
    return (
      <span className="relative block h-7 w-12 shrink-0 overflow-hidden rounded-xs border border-line bg-surface-raised">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={entry.thumbnailUrl}
          alt=""
          loading="lazy"
          decoding="async"
          className="h-full w-full object-cover"
        />
      </span>
    );
  }
  return (
    <span className="flex h-7 w-12 shrink-0 items-center justify-center rounded-xs border border-line bg-surface-raised">
      <DeckDocIcon className="size-3.5 text-doc-deck" />
    </span>
  );
}

export function EntryFilterChips({
  value,
  onChange,
}: {
  value: EntryFilter;
  onChange: (next: EntryFilter) => void;
}) {
  const options: Array<{ key: EntryFilter; label: string }> = [
    { key: 'all', label: 'All' },
    { key: 'deck', label: 'Decks' },
    { key: 'workbook', label: 'Sheets' },
    { key: 'file', label: 'Files' },
  ];
  return (
    <div className="flex items-center gap-1">
      {options.map((option) => (
        <Button
          key={option.key}
          size="sm"
          variant={value === option.key ? 'secondary' : 'tertiary'}
          onClick={() => onChange(option.key)}
        >
          {option.label}
        </Button>
      ))}
    </div>
  );
}

const MARQUEE_DRAG_THRESHOLD = 4;
const MARQUEE_SCROLL_ZONE = 32;
const MARQUEE_SCROLL_MAX_STEP = 16;

interface MarqueeSession {
  pointerId: number;
  // Content coordinates (relative to the scrollable content, not the
  // viewport), so the anchor stays put while the container scrolls.
  startX: number;
  startY: number;
  lastClientX: number;
  lastClientY: number;
  additive: boolean;
  baseSelection: Set<string>;
  active: boolean;
  scrollRaf: number | null;
  // Marquee hits in document (= entries) order, for anchor/focus on release.
  lastHits: string[];
}

export function EntryList({
  spaceId,
  locationKey,
  entries,
  isLoading,
  canWriteEntry = () => false,
  isDemoSpace = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  onFetchNextPage,
  onDragStart,
  onDragEnd,
  footer,
  emptyState,
}: EntryListProps) {
  const router = useRouter();
  const qc = useQueryClient();
  const { getAccessToken } = useAccessToken();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // anchor = fixed end of shift ranges; focused = the row keyboard nav moves.
  const [anchorId, setAnchorId] = useState<string | null>(null);
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [entriesToMove, setEntriesToMove] = useState<FileEntry[]>([]);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [entriesToDelete, setEntriesToDelete] = useState<FileEntry[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [marqueeRect, setMarqueeRect] = useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const marqueeRef = useRef<MarqueeSession | null>(null);

  // Don't flash a skeleton when contents load near-instantly (e.g. cached
  // folder switches) — only show it once loading outlasts the delay.
  const showSkeleton = useDelayedFlag(isLoading);

  const selectedEntries = entries.filter((entry) => selectedIds.has(entry.id));
  const writableSelected = selectedEntries.filter((entry) =>
    canWriteEntry(entry)
  );
  const downloadableSelected = selectedEntries.filter(
    (entry) => entry.kind === 'file' && entry.status === 'ready'
  );

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setAnchorId(null);
    setFocusedId(null);
  }, []);

  // The selection belongs to the location it was made in.
  useEffect(() => {
    clearSelection();
  }, [locationKey, clearSelection]);

  // Prune ids that no longer exist (deleted or moved away). Pagination only
  // appends, so this never drops live selections.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const liveIds = new Set(entries.map((entry) => entry.id));
      const next = new Set([...prev].filter((id) => liveIds.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [entries]);

  function entryEndpoint(entry: FileEntry): string {
    switch (entry.kind) {
      case 'deck':
        return `${BACKEND_URL}/decks/${entry.id}`;
      case 'workbook':
        return `${BACKEND_URL}/workbooks/${entry.id}`;
      case 'file':
        return `${BACKEND_URL}/spaces/${entry.spaceId}/files/uploads/${entry.id}`;
    }
  }

  function entryHref(entry: FileEntry): string {
    switch (entry.kind) {
      case 'deck':
        return `/decks/${entry.id}/edit`;
      case 'workbook':
        return `/sheets/${entry.id}/edit`;
      case 'file':
        return fileViewHref(entry.id);
    }
  }

  function openEntry(entry: FileEntry) {
    router.push(entryHref(entry));
  }

  const invalidateEntrySpaces = (list: FileEntry[]) => {
    const spaces = new Set(list.map((entry) => entry.spaceId));
    for (const sid of spaces) {
      qc.invalidateQueries({ queryKey: ['space', sid, 'files'] });
      qc.invalidateQueries({ queryKey: ['workbooks', sid] });
    }
    qc.invalidateQueries({ queryKey: ['all-documents'] });
  };

  const renameMutation = useMutation({
    mutationFn: async ({ entry, name }: { entry: FileEntry; name: string }) => {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(entryEndpoint(entry), {
        method: 'PATCH',
        headers: authHeaders(token),
        body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error('Failed to rename');
      return unwrapLambdaResponse(await res.json());
    },
    onSuccess: (_data, { entry }) => {
      invalidateEntrySpaces([entry]);
      setRenamingId(null);
    },
    onError: (error) => {
      reportError(error, { site: 'entry_rename', space_id: spaceId });
      toast.error('Failed to rename');
      setRenamingId(null);
    },
  });

  const moveMutation = useMutation({
    mutationFn: async ({
      list,
      target,
    }: {
      list: FileEntry[];
      target: PickerTarget;
    }) => {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const results = await Promise.allSettled(
        list.map(async (entry) => {
          const res = await fetch(entryEndpoint(entry), {
            method: 'PATCH',
            headers: authHeaders(token),
            // spaceId only differs on a cross-space move; the backend ignores
            // it when it matches the entry's current space.
            body: JSON.stringify({
              spaceId: target.spaceId,
              folderId: target.folderId,
            }),
          });
          if (res.status === 403) throw new Error('no_access');
          if (res.status === 409) throw new Error('agent_run_active');
          if (!res.ok) throw new Error('Failed to move');
          return unwrapLambdaResponse(await res.json());
        })
      );
      return { results };
    },
    onSuccess: ({ results }, { list, target }) => {
      // Both the source and destination spaces (and the aggregate) change.
      invalidateEntrySpaces(list);
      qc.invalidateQueries({ queryKey: ['space', target.spaceId, 'files'] });
      for (const entry of list) {
        // The moved doc's cached location is now stale. The editor tab reads
        // the space from the document summary, and the file viewer's view
        // token is scoped to `/spaces/:spaceId` — both keep pointing at the
        // old space (403s after the move) until invalidated.
        qc.invalidateQueries({
          queryKey: documentSummaryQueryKey(entry.id, entry.kind),
        });
        if (entry.kind === 'file') {
          qc.invalidateQueries({ queryKey: ['file', entry.id, 'view-token'] });
        }
      }
      setMoveDialogOpen(false);

      const failures = results.filter((r) => r.status === 'rejected');
      if (failures.length === 0) return;
      const reasons = new Set(
        failures.map((f) =>
          f.status === 'rejected' && f.reason instanceof Error
            ? f.reason.message
            : 'unknown'
        )
      );
      if (reasons.has('no_access')) {
        toast.error("You don't have edit access to that space.");
      } else if (reasons.has('agent_run_active')) {
        toast.error(
          'Pitchcrow is working on some of these documents. Move them after the run finishes.'
        );
      } else {
        toast.error(
          list.length > 1
            ? `Failed to move ${countLabel(failures.length, 'item')}`
            : 'Failed to move'
        );
      }
    },
    onError: (error) => {
      reportError(error, { site: 'entry_move', space_id: spaceId });
      toast.error('Failed to move');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (list: FileEntry[]) => {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const results = await Promise.allSettled(
        list.map(async (entry) => {
          const res = await fetch(entryEndpoint(entry), {
            method: 'DELETE',
            headers: authHeaders(token),
          });
          if (res.status === 409) throw new Error('agent_run_active');
          if (!res.ok) throw new Error('Failed to delete');
          return unwrapLambdaResponse(await res.json());
        })
      );
      return { results };
    },
    onSuccess: ({ results }, list) => {
      invalidateEntrySpaces(list);
      setDeleteDialogOpen(false);
      const busy = results.filter(
        (r) =>
          r.status === 'rejected' &&
          r.reason instanceof Error &&
          r.reason.message === 'agent_run_active'
      ).length;
      const failed =
        results.filter((r) => r.status === 'rejected').length - busy;
      const succeeded = list.length - busy - failed;
      if (busy > 0) {
        toast.error(
          `Pitchcrow is working on ${countLabel(busy, 'document')} — stop the run first.`
        );
      }
      if (failed > 0) {
        toast.error(`Failed to delete ${countLabel(failed, 'item')}`);
      }
    },
    onError: (error) => {
      reportError(error, { site: 'entry_delete', space_id: spaceId });
      toast.error('Failed to delete');
    },
  });

  const convertMutation = useMutation({
    mutationFn: async ({
      entrySpaceId,
      fileId,
      retry,
    }: {
      entrySpaceId: string;
      fileId: string;
      retry: boolean;
    }) => {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const path = retry ? 'deck-import/retry' : 'deck-import';
      const res = await fetch(
        `${BACKEND_URL}/spaces/${entrySpaceId}/files/${fileId}/${path}`,
        { method: 'POST', headers: authHeaders(token) }
      );
      if (res.status === 422) throw new Error('not_presentation_shaped');
      if (!res.ok) throw new Error('Failed to start conversion');
      return unwrapLambdaResponse(await res.json());
    },
    onSuccess: (_data, { entrySpaceId }) => {
      qc.invalidateQueries({ queryKey: ['space', entrySpaceId, 'files'] });
      qc.invalidateQueries({ queryKey: ['all-documents'] });
      toast.success('Converting to a Pitchcrow deck — this can take a few minutes.');
    },
    onError: (error) => {
      if (error instanceof Error && error.message === 'not_presentation_shaped') {
        toast.error('This file is not a slide presentation, so it cannot be converted.');
        return;
      }
      reportError(error, { site: 'deck_import_convert', space_id: spaceId });
      toast.error('Failed to start conversion');
    },
  });

  const retryProcessingMutation = useMutation({
    mutationFn: async ({
      entrySpaceId,
      fileId,
    }: {
      entrySpaceId: string;
      fileId: string;
    }) => {
      const token = await getAccessToken();
      if (!token) throw new Error('Not authenticated');
      const res = await fetch(
        `${BACKEND_URL}/spaces/${entrySpaceId}/files/uploads/${fileId}/retry`,
        { method: 'POST', headers: authHeaders(token) }
      );
      if (!res.ok) throw new Error('Failed to retry');
      return unwrapLambdaResponse(await res.json());
    },
    onSuccess: (_data, { entrySpaceId }) => {
      qc.invalidateQueries({ queryKey: ['space', entrySpaceId, 'files'] });
      qc.invalidateQueries({ queryKey: ['all-documents'] });
    },
    onError: (error) => {
      reportError(error, { site: 'file_processing_retry', space_id: spaceId });
      toast.error('Failed to retry');
    },
  });

  const fetchDownloadUrl = async (entry: FileEntry): Promise<string> => {
    const token = await getAccessToken();
    if (!token) throw new Error('Not authenticated');
    const res = await fetch(`${entryEndpoint(entry)}/download`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('Failed to get download URL');
    const data = unwrapLambdaResponse<{ downloadUrl: string }>(
      await res.json()
    );
    return data.downloadUrl;
  };

  const handleDownload = async (entry: FileEntry) => {
    try {
      window.open(await fetchDownloadUrl(entry), '_blank');
    } catch (error) {
      reportError(error, { site: 'entry_download', entry_id: entry.id });
      toast.error('Failed to download file');
    }
  };

  // Anchor clicks instead of window.open: popup blockers allow at most one
  // new tab per gesture, while successive downloads only need the browser's
  // multiple-downloads consent.
  const handleBulkDownload = async (list: FileEntry[]) => {
    let failed = 0;
    for (const entry of list) {
      try {
        const url = await fetchDownloadUrl(entry);
        const a = document.createElement('a');
        a.href = url;
        a.download = entry.name;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        a.remove();
        await new Promise((resolve) => setTimeout(resolve, 200));
      } catch (error) {
        failed += 1;
        reportError(error, { site: 'entry_download', entry_id: entry.id });
      }
    }
    if (failed > 0) {
      toast.error(`Failed to download ${countLabel(failed, 'file')}`);
    }
  };

  const startRename = (entry: FileEntry) => {
    // Deferred one tick: the closing context/dropdown menu restores focus on
    // teardown, which would immediately blur the inline input.
    setTimeout(() => {
      setRenamingId(entry.id);
      setRenameValue(entry.name);
    }, 0);
  };

  const commitRename = (entry: FileEntry) => {
    if (renamingId !== entry.id || renameMutation.isPending) return;
    const name = renameValue.trim();
    if (!name || name === entry.name) {
      setRenamingId(null);
      return;
    }
    renameMutation.mutate({ entry, name });
  };

  const requestDelete = useCallback(
    (list: FileEntry[]) => {
      if (list.length === 0) return;
      setEntriesToDelete(list);
      setDeleteDialogOpen(true);
    },
    []
  );

  const requestMove = (list: FileEntry[]) => {
    if (list.length === 0) return;
    setEntriesToMove(list);
    setMoveDialogOpen(true);
  };

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !onFetchNextPage) return;
    const observer = new IntersectionObserver(
      (observed) => {
        if (observed[0]?.isIntersecting && hasNextPage && !isFetchingNextPage) {
          onFetchNextPage();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, onFetchNextPage]);

  const entryIndex = (id: string | null) =>
    id ? entries.findIndex((entry) => entry.id === id) : -1;

  const rangeIds = (fromIndex: number, toIndex: number): string[] => {
    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);
    return entries.slice(start, end + 1).map((entry) => entry.id);
  };

  const handleRowClick = (e: React.MouseEvent, entry: FileEntry, index: number) => {
    if (e.shiftKey && anchorId && entryIndex(anchorId) !== -1) {
      const range = rangeIds(entryIndex(anchorId), index);
      setSelectedIds((prev) => {
        // Cmd+Shift adds the range to the selection; plain Shift re-draws
        // the range from the anchor (Finder semantics).
        const next = e.metaKey || e.ctrlKey ? new Set(prev) : new Set<string>();
        range.forEach((id) => next.add(id));
        return next;
      });
      setFocusedId(entry.id);
    } else if (e.metaKey || e.ctrlKey) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        if (next.has(entry.id)) {
          next.delete(entry.id);
        } else {
          next.add(entry.id);
        }
        return next;
      });
      setAnchorId(entry.id);
      setFocusedId(entry.id);
    } else {
      setSelectedIds(new Set([entry.id]));
      setAnchorId(entry.id);
      setFocusedId(entry.id);
    }
  };

  // A right-click outside the selection retargets it (Finder semantics), so
  // the menu always describes what it will act on.
  const handleRowContextMenu = (entry: FileEntry) => {
    if (!selectedIds.has(entry.id)) {
      setSelectedIds(new Set([entry.id]));
      setAnchorId(entry.id);
      setFocusedId(entry.id);
    }
  };

  const scrollRowIntoView = (id: string) => {
    contentRef.current
      ?.querySelector(`[data-entry-id="${CSS.escape(id)}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  };

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      const target = e.target instanceof Element ? e.target : null;
      // Leave typing surfaces and open overlays alone — Cmd+A in the search
      // box must select its text, not the file list.
      if (
        target?.closest(
          'input, textarea, select, [contenteditable="true"], [role="dialog"], [role="alertdialog"], [role="menu"]'
        )
      ) {
        return;
      }
      if (renamingId || moveDialogOpen || deleteDialogOpen) return;
      const isMod = e.metaKey || e.ctrlKey;
      // Enter/Delete/arrows belong to the focused control when the user has
      // tabbed onto one (Enter on "Advanced Search" must click it, not open
      // the selected row). Cmd+A and Escape stay list-global, as in
      // Finder/Drive.
      const onControl = Boolean(
        target?.closest('button, a, select, [role="button"], [role="menuitem"]')
      );

      if (isMod && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'a') {
        if (entries.length === 0) return;
        e.preventDefault();
        setSelectedIds(new Set(entries.map((entry) => entry.id)));
        setAnchorId(entries[0].id);
        setFocusedId(entries[entries.length - 1].id);
        return;
      }
      if (e.key === 'Escape') {
        if (selectedIds.size > 0) {
          e.preventDefault();
          clearSelection();
        }
        return;
      }
      if (e.key === 'Delete' || (e.key === 'Backspace' && isMod)) {
        if (!onControl && writableSelected.length > 0) {
          e.preventDefault();
          requestDelete(writableSelected);
        }
        return;
      }
      if (e.key === 'Enter') {
        if (!onControl && selectedEntries.length === 1) {
          e.preventDefault();
          openEntry(selectedEntries[0]);
        }
        return;
      }
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        if (onControl || entries.length === 0) return;
        e.preventDefault();
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        const focusIdx = entryIndex(focusedId);
        const nextIdx =
          focusIdx === -1
            ? delta === 1
              ? 0
              : entries.length - 1
            : Math.max(0, Math.min(entries.length - 1, focusIdx + delta));
        const nextId = entries[nextIdx].id;
        if (e.shiftKey) {
          const anchorIdx =
            entryIndex(anchorId) !== -1 ? entryIndex(anchorId) : nextIdx;
          setSelectedIds(new Set(rangeIds(anchorIdx, nextIdx)));
          if (entryIndex(anchorId) === -1) setAnchorId(nextId);
        } else {
          setSelectedIds(new Set([nextId]));
          setAnchorId(nextId);
        }
        setFocusedId(nextId);
        scrollRowIntoView(nextId);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  });

  // --- Marquee (rubber-band) selection --------------------------------------
  // Starts only on empty space: pointer-downs on rows belong to row drag
  // (drag-to-move into the tree) and clicks. Coordinates are kept relative to
  // the content wrapper so the rectangle's anchor survives auto-scrolling.

  const toContentPoint = (clientX: number, clientY: number) => {
    const rect = contentRef.current!.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const updateMarqueeSelection = () => {
    const m = marqueeRef.current;
    const content = contentRef.current;
    if (!m || !m.active || !content) return;
    const point = toContentPoint(m.lastClientX, m.lastClientY);
    const rect = {
      left: Math.min(m.startX, point.x),
      top: Math.min(m.startY, point.y),
      width: Math.abs(point.x - m.startX),
      height: Math.abs(point.y - m.startY),
    };
    setMarqueeRect(rect);

    const contentBox = content.getBoundingClientRect();
    const hits: string[] = [];
    content
      .querySelectorAll<HTMLElement>('[data-entry-id]')
      .forEach((row) => {
        const rowBox = row.getBoundingClientRect();
        const rowTop = rowBox.top - contentBox.top;
        const rowLeft = rowBox.left - contentBox.left;
        const intersects =
          rect.left < rowLeft + rowBox.width &&
          rect.left + rect.width > rowLeft &&
          rect.top < rowTop + rowBox.height &&
          rect.top + rect.height > rowTop;
        if (intersects) hits.push(row.dataset.entryId!);
      });
    m.lastHits = hits;
    setSelectedIds(
      m.additive ? new Set([...m.baseSelection, ...hits]) : new Set(hits)
    );
  };

  const stopMarqueeAutoScroll = () => {
    const m = marqueeRef.current;
    if (m?.scrollRaf != null) {
      cancelAnimationFrame(m.scrollRaf);
      m.scrollRaf = null;
    }
  };

  const marqueeAutoScrollTick = () => {
    const m = marqueeRef.current;
    const scroller = scrollRef.current;
    if (!m || !m.active || !scroller) return;
    const box = scroller.getBoundingClientRect();
    let step = 0;
    if (m.lastClientY < box.top + MARQUEE_SCROLL_ZONE) {
      step = -Math.ceil(
        ((box.top + MARQUEE_SCROLL_ZONE - m.lastClientY) / MARQUEE_SCROLL_ZONE) *
          MARQUEE_SCROLL_MAX_STEP
      );
    } else if (m.lastClientY > box.bottom - MARQUEE_SCROLL_ZONE) {
      step = Math.ceil(
        ((m.lastClientY - (box.bottom - MARQUEE_SCROLL_ZONE)) /
          MARQUEE_SCROLL_ZONE) *
          MARQUEE_SCROLL_MAX_STEP
      );
    }
    if (step !== 0) {
      scroller.scrollTop += step;
      updateMarqueeSelection();
      m.scrollRaf = requestAnimationFrame(marqueeAutoScrollTick);
    } else {
      m.scrollRaf = null;
    }
  };

  const ensureMarqueeAutoScroll = () => {
    const m = marqueeRef.current;
    if (m && m.active && m.scrollRaf == null) {
      m.scrollRaf = requestAnimationFrame(marqueeAutoScrollTick);
    }
  };

  useEffect(() => () => stopMarqueeAutoScroll(), []);

  const handleMarqueePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const target = e.target as HTMLElement;
    // React bubbles synthetic events from portals (context menus, dialogs)
    // through this handler even though their DOM nodes live under body.
    // Capturing those pointers would steal the menu item's pointerup — the
    // action never fires and the release reads as a click-on-empty (clearing
    // the selection). Only presses physically inside the scroller count.
    if (!scrollRef.current?.contains(target)) return;
    if (target.closest('tr, button, input, a')) return;
    // A press on any scrollbar pans, it doesn't select. The outer container
    // and the Table's own overflow-x wrapper both scroll, so walk up from
    // the press target and bail when the point falls outside an element's
    // client box (i.e. on its scrollbar).
    for (
      let node: HTMLElement | null = target;
      node;
      node = node === scrollRef.current ? null : node.parentElement
    ) {
      if (
        node.scrollWidth > node.clientWidth ||
        node.scrollHeight > node.clientHeight
      ) {
        const box = node.getBoundingClientRect();
        if (
          e.clientX - box.left - node.clientLeft >= node.clientWidth ||
          e.clientY - box.top - node.clientTop >= node.clientHeight
        ) {
          return;
        }
      }
    }
    const point = toContentPoint(e.clientX, e.clientY);
    const additive = e.shiftKey || e.metaKey || e.ctrlKey;
    marqueeRef.current = {
      pointerId: e.pointerId,
      startX: point.x,
      startY: point.y,
      lastClientX: e.clientX,
      lastClientY: e.clientY,
      additive,
      baseSelection: additive ? new Set(selectedIds) : new Set(),
      active: false,
      scrollRaf: null,
      lastHits: [],
    };
    scrollRef.current?.setPointerCapture(e.pointerId);
  };

  const handleMarqueePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const m = marqueeRef.current;
    if (!m || m.pointerId !== e.pointerId) return;
    m.lastClientX = e.clientX;
    m.lastClientY = e.clientY;
    if (!m.active) {
      const point = toContentPoint(e.clientX, e.clientY);
      if (
        Math.abs(point.x - m.startX) < MARQUEE_DRAG_THRESHOLD &&
        Math.abs(point.y - m.startY) < MARQUEE_DRAG_THRESHOLD
      ) {
        return;
      }
      m.active = true;
    }
    updateMarqueeSelection();
    ensureMarqueeAutoScroll();
  };

  const handleMarqueePointerEnd = (e: React.PointerEvent<HTMLDivElement>) => {
    const m = marqueeRef.current;
    if (!m || m.pointerId !== e.pointerId) return;
    stopMarqueeAutoScroll();
    // A press on empty space that never became a drag is a click: clear.
    if (!m.active && !m.additive) {
      clearSelection();
    }
    // Hand the range anchor and keyboard focus to the marquee result so
    // shift-click and arrows continue from it, not from a stale row.
    if (m.active && m.lastHits.length > 0) {
      setAnchorId(m.lastHits[0]);
      setFocusedId(m.lastHits[m.lastHits.length - 1]);
    }
    marqueeRef.current = null;
    setMarqueeRect(null);
    if (scrollRef.current?.hasPointerCapture(e.pointerId)) {
      scrollRef.current.releasePointerCapture(e.pointerId);
    }
  };

  if (isLoading) {
    return showSkeleton ? (
      <div className="space-y-2 p-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    ) : null;
  }

  if (entries.length === 0 && (emptyState || footer)) {
    return (
      <div className="h-full overflow-auto">
        {emptyState}
        {footer}
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center px-4 py-16 text-center">
        <div className="bg-primary/10 text-primary mb-4 flex size-12 items-center justify-center rounded-full">
          <IconInbox className="size-6" />
        </div>
        <p className="text-lg font-medium">Nothing here yet</p>
        <p className="text-muted-foreground mt-2 text-base">
          Create a deck or sheet, or upload files to get started
        </p>
      </div>
    );
  }

  // One action list per entry, rendered into both the context menu and the
  // ⋮ dropdown so the two surfaces can never drift apart.
  type EntryAction =
    | { type: 'separator'; key: string }
    | {
        type: 'item';
        key: string;
        label: string;
        icon: React.ComponentType<{ className?: string }>;
        onSelect: () => void;
        destructive?: boolean;
        disabled?: boolean;
      };

  const actionsFor = (entry: FileEntry): EntryAction[] => {
    const canWrite = canWriteEntry(entry);
    const actions: EntryAction[] = [];
    if (entry.kind === 'file') {
      actions.push({
        type: 'item',
        key: 'view',
        label: 'View',
        icon: IconEye,
        onSelect: () => router.push(fileViewHref(entry.id)),
      });
      actions.push({
        type: 'item',
        key: 'download',
        label: 'Download',
        icon: IconDownload,
        onSelect: () => void handleDownload(entry),
        disabled: entry.status !== 'ready',
      });
      const imp = entry.deckImport;
      const ext = (entry.fileType || entry.name.split('.').pop() || '')
        .toLowerCase();
      const isPresentation = ['pptx', 'ppt', 'pdf'].includes(ext);
      if (imp?.status === 'ready' && imp.deckId) {
        actions.push({
          type: 'item',
          key: 'open-deck',
          label: 'Open deck',
          icon: DeckDocIcon,
          onSelect: () => router.push(`/decks/${imp.deckId}/edit`),
        });
      } else if (imp && imp.status !== 'failed') {
        actions.push({
          type: 'item',
          key: 'converting',
          label: 'Converting to deck…',
          icon: IconSparkles,
          onSelect: () => {},
          disabled: true,
        });
      } else if (canWrite && isPresentation) {
        actions.push({
          type: 'item',
          key: 'convert',
          label: imp?.status === 'failed' ? 'Retry conversion' : 'Convert to deck',
          icon: IconSparkles,
          onSelect: () =>
            convertMutation.mutate({
              entrySpaceId: entry.spaceId,
              fileId: entry.id,
              retry: imp?.status === 'failed',
            }),
        });
      }
    } else {
      actions.push({
        type: 'item',
        key: 'open',
        label: 'Open',
        icon: IconExternalLink,
        onSelect: () => openEntry(entry),
      });
    }
    if (canWrite) {
      actions.push({ type: 'separator', key: 'sep-write' });
      actions.push({
        type: 'item',
        key: 'rename',
        label: 'Rename',
        icon: IconPencil,
        onSelect: () => startRename(entry),
      });
      actions.push({
        type: 'item',
        key: 'move',
        label: 'Move to Folder',
        icon: IconFolderSymlink,
        onSelect: () => requestMove([entry]),
      });
      actions.push({ type: 'separator', key: 'sep-delete' });
      actions.push({
        type: 'item',
        key: 'delete',
        label: 'Delete',
        icon: IconTrash,
        destructive: true,
        onSelect: () => requestDelete([entry]),
      });
    }
    return actions;
  };

  // When the right-clicked row is part of a multi-selection, the menu acts on
  // the whole selection instead of the row under the cursor.
  const bulkActions = (): EntryAction[] => {
    const actions: EntryAction[] = [];
    if (downloadableSelected.length > 0) {
      actions.push({
        type: 'item',
        key: 'bulk-download',
        label: `Download ${countLabel(downloadableSelected.length, 'file')}`,
        icon: IconDownload,
        onSelect: () => void handleBulkDownload(downloadableSelected),
      });
    }
    if (writableSelected.length > 0) {
      if (actions.length > 0) {
        actions.push({ type: 'separator', key: 'sep-write' });
      }
      actions.push({
        type: 'item',
        key: 'bulk-move',
        label: `Move ${countLabel(writableSelected.length, 'item')}`,
        icon: IconFolderSymlink,
        onSelect: () => requestMove(writableSelected),
      });
      actions.push({ type: 'separator', key: 'sep-delete' });
      actions.push({
        type: 'item',
        key: 'bulk-delete',
        label: `Delete ${countLabel(writableSelected.length, 'item')}`,
        icon: IconTrash,
        destructive: true,
        onSelect: () => requestDelete(writableSelected),
      });
    }
    if (actions.length === 0) {
      actions.push({
        type: 'item',
        key: 'bulk-none',
        label: `${countLabel(selectedEntries.length, 'item')} selected`,
        icon: IconInbox,
        onSelect: () => {},
        disabled: true,
      });
    }
    return actions;
  };

  const anyWritable = entries.some((entry) => canWriteEntry(entry));

  const renderMenuActions = (
    actions: EntryAction[],
    Kind: 'context' | 'dropdown'
  ) =>
    actions.map((action) =>
      action.type === 'separator' ? (
        Kind === 'context' ? (
          <ContextMenuSeparator key={action.key} />
        ) : (
          <DropdownMenuSeparator key={action.key} />
        )
      ) : Kind === 'context' ? (
        <ContextMenuItem
          key={action.key}
          onClick={action.onSelect}
          disabled={action.disabled}
          variant={action.destructive ? 'destructive' : 'default'}
        >
          <action.icon className="size-4" />
          {action.label}
        </ContextMenuItem>
      ) : (
        <DropdownMenuItem
          key={action.key}
          onClick={action.onSelect}
          disabled={action.disabled}
          className={cn(action.destructive && 'text-destructive')}
        >
          <action.icon className="mr-2 size-4" />
          {action.label}
        </DropdownMenuItem>
      )
    );

  const deleteNoun =
    entriesToDelete.length === 1
      ? `"${entriesToDelete[0]?.name}"`
      : countLabel(entriesToDelete.length, 'item');

  return (
    <div className="relative h-full">
      <div
        ref={scrollRef}
        className="h-full overflow-auto"
        onPointerDown={handleMarqueePointerDown}
        onPointerMove={handleMarqueePointerMove}
        onPointerUp={handleMarqueePointerEnd}
        onPointerCancel={handleMarqueePointerEnd}
      >
        {/* min-h-full makes the blank area below the last row part of the
            selection surface (click to clear, drag to marquee). Text
            selection is off: drags here are selection gestures. */}
        <div ref={contentRef} className="relative min-h-full select-none">
          <Table className="[&_td:first-child]:pl-4 [&_td:last-child]:pr-4 [&_th:first-child]:pl-4 [&_th:last-child]:pr-4">
            <TableHeader>
              <TableRow>
                {anyWritable && <TableHead className="w-8"></TableHead>}
                <TableHead>Name</TableHead>
                <TableHead className="w-24">Kind</TableHead>
                <TableHead className="w-32">Updated</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((entry, index) => {
                const isBulkTarget =
                  selectedIds.has(entry.id) && selectedEntries.length > 1;
                const actions = isBulkTarget ? bulkActions() : actionsFor(entry);
                const rowActions = actionsFor(entry);
                const canWrite = canWriteEntry(entry);
                return (
                  <ContextMenu key={`${entry.kind}-${entry.id}`}>
                    <ContextMenuTrigger asChild>
                      <TableRow
                        data-entry-id={entry.id}
                        data-tour={
                          isDemoSpace && entry.kind === 'workbook'
                            ? 'demo-doc-sheet'
                            : isDemoSpace && entry.kind === 'deck'
                              ? 'demo-doc-deck'
                              : undefined
                        }
                        data-doc-id={
                          isDemoSpace &&
                          (entry.kind === 'workbook' || entry.kind === 'deck')
                            ? entry.id
                            : undefined
                        }
                        className={cn(
                          'cursor-pointer hover:bg-muted/50',
                          selectedIds.has(entry.id) && 'bg-muted'
                        )}
                        onClick={(e) => handleRowClick(e, entry, index)}
                        onContextMenu={() => handleRowContextMenu(entry)}
                        onDoubleClick={() => openEntry(entry)}
                        // Rows open by code, not <Link>, so nothing prefetches
                        // the editor's App Shell; do it on hover so the open
                        // is instant.
                        onPointerEnter={() =>
                          router.prefetch(entryHref(entry))
                        }
                        draggable={canWrite && renamingId !== entry.id}
                        onDragStart={(e) => {
                          if (!canWrite) return;
                          // If dragging a selected row, drag the whole
                          // selection — minus rows the user can't write
                          // (aggregate view mixes spaces with different
                          // access).
                          const toMove = (
                            selectedIds.has(entry.id)
                              ? entries.filter((d) => selectedIds.has(d.id))
                              : [entry]
                          ).filter((d) => canWriteEntry(d));
                          const payload: DraggedEntry[] = toMove.map((d) => ({
                            kind: d.kind,
                            id: d.id,
                            name: d.name,
                            spaceId: d.spaceId,
                            folderId: d.folderId,
                          }));
                          e.dataTransfer.setData(
                            DRAG_ENTRY_MIME,
                            JSON.stringify(payload)
                          );
                          e.dataTransfer.effectAllowed = 'move';
                          onDragStart?.();
                        }}
                        onDragEnd={() => {
                          onDragEnd?.();
                        }}
                      >
                        {anyWritable && (
                          <TableCell
                            className={cn(
                              canWrite && 'cursor-grab active:cursor-grabbing'
                            )}
                            onClick={(e) => e.stopPropagation()}
                          >
                            {canWrite && (
                              <IconGripVertical className="size-4 text-muted-foreground" />
                            )}
                          </TableCell>
                        )}
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <EntryIcon entry={entry} />
                            {renamingId === entry.id ? (
                              <Input
                                autoFocus
                                value={renameValue}
                                disabled={renameMutation.isPending}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onFocus={(e) => e.currentTarget.select()}
                                onClick={(e) => e.stopPropagation()}
                                onDoubleClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') commitRename(entry);
                                  if (e.key === 'Escape') setRenamingId(null);
                                }}
                                onBlur={() => commitRename(entry)}
                                className="h-7 max-w-xs select-text"
                                aria-label={`Rename ${entry.name}`}
                              />
                            ) : (
                              <>
                                <span className="truncate max-w-xs font-medium">
                                  {entry.name}
                                </span>
                                {entry.kind === 'file' && (
                                  <>
                                    <ProcessingStatusBadge
                                      status={entry.status}
                                      tooltip={
                                        FILE_STATUS_TOOLTIPS[entry.status]
                                      }
                                    />
                                    {entry.status === 'failed' &&
                                      canWriteEntry(entry) && (
                                        <button
                                          type="button"
                                          className="shrink-0 text-sm text-fg-secondary underline underline-offset-2 hover:text-fg disabled:text-fg-light disabled:no-underline"
                                          disabled={
                                            retryProcessingMutation.isPending &&
                                            retryProcessingMutation.variables
                                              ?.fileId === entry.id
                                          }
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            retryProcessingMutation.mutate({
                                              entrySpaceId: entry.spaceId,
                                              fileId: entry.id,
                                            });
                                          }}
                                          onDoubleClick={(e) =>
                                            e.stopPropagation()
                                          }
                                        >
                                          Retry
                                        </button>
                                      )}
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {entryKindLabel(entry)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatRelativeTime(entry.updatedAt)}
                        </TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="tertiary"
                                size="icon"
                                className="size-8"
                              >
                                <IconDotsVertical className="size-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {renderMenuActions(rowActions, 'dropdown')}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    </ContextMenuTrigger>
                    <ContextMenuContent>
                      {renderMenuActions(actions, 'context')}
                    </ContextMenuContent>
                  </ContextMenu>
                );
              })}
            </TableBody>
          </Table>

          {isFetchingNextPage && (
            <div className="space-y-2 p-4">
              {[1, 2].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}
          <div ref={sentinelRef} className="h-px w-full" aria-hidden />
          {footer}

          {marqueeRect && (
            <div
              className="pointer-events-none absolute z-10 border border-primary bg-primary/10"
              style={{
                left: marqueeRect.left,
                top: marqueeRect.top,
                width: marqueeRect.width,
                height: marqueeRect.height,
              }}
              aria-hidden
            />
          )}
        </div>
      </div>

      {selectedEntries.length >= 2 && (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center">
          <div className="rook-floating pointer-events-auto flex items-center gap-1 p-1">
            <span className="px-2 text-sm text-fg-secondary">
              {countLabel(selectedEntries.length, 'item')} selected
            </span>
            {downloadableSelected.length > 0 && (
              <Button
                size="sm"
                variant="tertiary"
                onClick={() => void handleBulkDownload(downloadableSelected)}
              >
                <IconDownload className="mr-1 size-4" />
                Download
              </Button>
            )}
            {writableSelected.length > 0 && (
              <>
                <Button
                  size="sm"
                  variant="tertiary"
                  onClick={() => requestMove(writableSelected)}
                >
                  <IconFolderSymlink className="mr-1 size-4" />
                  Move
                </Button>
                <Button
                  size="sm"
                  variant="tertiary"
                  className="text-destructive"
                  onClick={() => requestDelete(writableSelected)}
                >
                  <IconTrash className="mr-1 size-4" />
                  Delete
                </Button>
              </>
            )}
            <Button
              size="sm"
              variant="tertiary"
              onClick={clearSelection}
              aria-label="Clear selection"
            >
              <IconX className="size-4" />
            </Button>
          </div>
        </div>
      )}

      {/* The subject entries are kept through the close animation so the
          dialog copy doesn't blank out while it fades. Destination can be any
          folder in any space the user can edit. */}
      <MoveToSpaceDialog
        open={moveDialogOpen}
        onOpenChange={setMoveDialogOpen}
        subjectLabel={
          entriesToMove.length === 1
            ? `“${entriesToMove[0]?.name}”`
            : countLabel(entriesToMove.length, 'item')
        }
        initialTarget={commonLocation(entriesToMove)}
        defaultOpenSpaceId={entriesToMove[0]?.spaceId ?? spaceId ?? undefined}
        onMove={(target) => {
          if (entriesToMove.length > 0) {
            moveMutation.mutate({ list: entriesToMove, target });
          }
        }}
        isMoving={moveMutation.isPending}
      />

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete {deleteNoun}?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="secondary"
              onClick={() => setDeleteDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              accent="danger"
              onClick={() =>
                entriesToDelete.length > 0 &&
                deleteMutation.mutate(entriesToDelete)
              }
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function flattenEntryPages(
  pages: Array<{ entries: FileEntry[] }> | undefined
): FileEntry[] {
  return pages?.flatMap((page) => page.entries) ?? [];
}

export function fileViewHref(fileId: string): string {
  return `/files/${fileId}/view`;
}
